import { parseUrl, toNavigableUrl, toSameOriginPathname } from "@solvari/utils";

/**
 * One split-url decision for both sides: the server turns `redirect` into a 302, the client
 * navigates. Feature values arrive unvalidated (GrowthBook returns whatever the payload holds),
 * so the guards live here too — both sides must refuse the same values.
 */
type SplitUrlOutcome =
  | { clientSide: boolean; status: "redirect"; url: string }
  | { status: "none" }
  | { status: "not-in-test" }
  | { status: "unnavigable"; target: string };

type SplitUrlMapOutcome =
  | { status: "not-in-test" }
  | { status: "on-target" }
  | { status: "redirect"; target: string };

function asString(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function asStringRecord(value: unknown): Record<string, string> | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }

  // Empty map → null, not an empty test: else every pageview reports "not-in-test".
  const entries = Object.values(value);

  return entries.length > 0 &&
    entries.every((entry) => typeof entry === "string")
    ? (value as Record<string, string>)
    : null;
}

function resolveSplitUrl(
  splitUrl: string | null,
  location: URL,
): string | null {
  if (splitUrl === null) {
    return null;
  }

  return toSameOriginPathname(splitUrl, location.origin) === location.pathname
    ? null
    : splitUrl;
}

// "not-in-test": page in neither half of the map → the client reports it.
function resolveSplitUrlMap(
  splitUrlMap: Record<string, string> | null,
  location: URL,
): SplitUrlMapOutcome {
  if (splitUrlMap === null) {
    return { status: "on-target" };
  }

  // An unparseable entry compares as null, so it only ever fails to match
  const isCurrent = (value: string) =>
    toSameOriginPathname(value, location.origin) === location.pathname;

  const entries = Object.entries(splitUrlMap);
  if (entries.some(([, target]) => isCurrent(target))) {
    return { status: "on-target" };
  }

  const target = entries.find(([source]) => isCurrent(source))?.[1];

  return target === undefined
    ? { status: "not-in-test" }
    : { status: "redirect", target };
}

function toRedirect(target: string, origin: string): SplitUrlOutcome {
  const url = toNavigableUrl(target, origin);
  if (url === null) {
    return { status: "unnavigable", target };
  }

  // A path stays inside the SPA; an absolute url is a page load.
  return { clientSide: !/^https?:\/\//i.test(target), status: "redirect", url };
}

// Split-url decides whenever it names a target off the current path; the map only fills its silence.
function resolveSplitUrlOutcome({
  currentUrl,
  splitUrl,
  splitUrlMap,
}: {
  currentUrl: string;
  splitUrl: unknown;
  splitUrlMap: unknown;
}): SplitUrlOutcome {
  const location = parseUrl(currentUrl);
  if (location === null) {
    return { status: "none" };
  }

  const target = resolveSplitUrl(asString(splitUrl), location);
  if (target !== null) {
    return toRedirect(target, location.origin);
  }

  const outcome = resolveSplitUrlMap(asStringRecord(splitUrlMap), location);
  if (outcome.status === "on-target") {
    return { status: "none" };
  }
  if (outcome.status === "not-in-test") {
    return { status: "not-in-test" };
  }

  return toRedirect(outcome.target, location.origin);
}

export type { SplitUrlOutcome };
export { resolveSplitUrlOutcome };
